import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import PermissionGuard from '../../components/permissions/PermissionGuard'
import {
  HiOutlineArrowLeft,
  HiOutlineFolder,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineDocumentText,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineClock,
  HiOutlineEye,
  HiOutlineChatBubbleLeft,
} from 'react-icons/hi2'
import DocumentUploadModal from '../../components/documents/DocumentUploadModal'
import DocumentTable from '../../components/documents/DocumentTable'
import MessagePanel from '../../components/messages/MessagePanel'
import BlockedAction from '../../components/common/BlockedAction'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  PageHeader,
  Spinner,
} from '../../components/ui'

export default function CaseDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError, showConfirm } = useAlerts()
  const { getEntity } = useWorkspace()

  const [caseData, setCaseData] = useState(null)
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showMessagePanel, setShowMessagePanel] = useState(false)

  useEffect(() => {
    fetchCaseDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ownerAccountId])

  useEffect(() => {
    if (activeTab === 'sessions' && !sessionsLoading && sessions.length === 0) {
      fetchSessions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  async function fetchCaseDetails() {
    try {
      setLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.get(`/cases/${id}/`, { params })
      if (response.success) {
        setCaseData(response.data.data || response.data)
      } else {
        showError(t('cases.loadFailed', { entity: getEntity('case').singular.toLowerCase() }))
        navigate('/cases')
      }
    } catch {
      showError(t('cases.loadFailed', { entity: getEntity('case').singular.toLowerCase() }))
      navigate('/cases')
    } finally {
      setLoading(false)
    }
  }

  async function fetchSessions() {
    try {
      setSessionsLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId
        ? { owner_account_id: ownerAccountId, case_id: id }
        : { case_id: id }
      const response = await apiService.get('/sessions/', { params })
      if (response.success) {
        const data = response.data.data || response.data
        setSessions(data.data || data.results || [])
      }
    } catch { /* swallow — empty list renders */ } finally {
      setSessionsLoading(false)
    }
  }

  const handleDeleteCase = async () => {
    const confirmed = await showConfirm(
      t('cases.deleteTitle', { entity: getEntity('case').singular }),
      t('cases.deleteMessage', {
        title: caseData?.title,
        sessions: getEntity('session').plural.toLowerCase(),
        documents: getEntity('document').plural.toLowerCase(),
      }),
      t('common.delete'),
      t('common.cancel'),
      'destructive',
    )
    if (!confirmed) return
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/cases/${id}/`, { params })
      if (response.success) {
        showSuccess(t('cases.deleteSuccess', { entity: getEntity('case').singular }))
        navigate('/cases')
      } else {
        showError(t('cases.deleteFailed', { entity: getEntity('case').singular.toLowerCase() }))
      }
    } catch {
      showError(t('cases.deleteFailed', { entity: getEntity('case').singular.toLowerCase() }))
    }
  }

  const formatDate = (s) => s
    ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'N/A'

  const formatDateTime = (s) => s
    ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'N/A'

  const tabs = [
    { id: 'overview',  label: t('cases.detail.tabOverview'),                                       icon: HiOutlineEye,          count: null },
    { id: 'sessions',  label: t('cases.detail.tabSessions',  { entity: getEntity('session').plural }),  icon: HiOutlineClock,        count: caseData?.sessions_count || 0 },
    { id: 'documents', label: t('cases.detail.tabDocuments', { entity: getEntity('document').plural }), icon: HiOutlineDocumentText, count: caseData?.documents_count || 0 },
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-muted rounded-xl animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="w-48 h-7 bg-muted rounded animate-pulse" />
            <div className="w-96 h-4 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <Card padding="md"><div className="h-64 bg-muted rounded-lg animate-pulse" /></Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  if (!caseData) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={HiOutlineFolder}
          title={t('cases.detail.notFound', { entity: getEntity('case').singular })}
          description={t('cases.detail.notFoundDescription', { entity: getEntity('case').singular.toLowerCase() })}
          action={
            <Button leadingIcon={HiOutlineArrowLeft} onClick={() => navigate('/cases')}>
              {t('cases.detail.backTo', { entity: getEntity('case').plural })}
            </Button>
          }
        />
      </Card>
    )
  }

  return (
    <div className={`space-y-6 transition-all duration-300 ${showMessagePanel ? 'me-0 sm:me-[400px] md:me-[420px] lg:me-[450px] xl:me-[480px]' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <IconButton
            icon={HiOutlineArrowLeft}
            aria-label={t('cases.detail.backTo', { entity: getEntity('case').plural })}
            variant="ghost"
            onClick={() => navigate('/cases')}
            className="rtl:rotate-180"
          />
          <span className="w-12 h-12 bg-info-soft text-info rounded-xl flex items-center justify-center flex-shrink-0">
            <HiOutlineFolder className="w-6 h-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-ink-primary truncate">{caseData.title}</h1>
            <p className="text-sm text-ink-muted">
              {isWorkspaceSwitched
                ? t('cases.detail.subtitleSwitched', { entity: getEntity('case').singular, name: user?.switchedAccountName })
                : t('cases.detail.createdOn', { date: formatDate(caseData.created_at) })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={showMessagePanel ? 'primary' : 'secondary'}
            leadingIcon={HiOutlineChatBubbleLeft}
            onClick={() => setShowMessagePanel(v => !v)}
          >
            {t('nav.messages')}
          </Button>
          <PermissionGuard resource="case" action="update">
            <Button variant="secondary" leadingIcon={HiOutlinePencilSquare} onClick={() => navigate(`/cases/${id}/edit`)}>
              {t('cases.detail.editCase', { entity: getEntity('case').singular })}
            </Button>
          </PermissionGuard>
          <PermissionGuard resource="case" action="delete">
            <Button variant="secondary" leadingIcon={HiOutlineTrash} onClick={handleDeleteCase} className="!text-danger hover:!bg-danger-soft hover:!border-danger/40">
              {t('common.delete')}
            </Button>
          </PermissionGuard>
        </div>
      </div>

      <Card padding="none">
        <nav className="flex gap-1 px-3 sm:px-5 border-b border-border-subtle overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const active = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-3 -mb-px border-b-2 font-medium text-sm inline-flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active
                    ? 'border-brand text-brand'
                    : 'border-transparent text-ink-secondary hover:text-ink-primary hover:border-border-strong'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <Badge tone={active ? 'brand' : 'neutral'} size="sm">{tab.count}</Badge>
                )}
              </button>
            )
          })}
        </nav>

        <div className="p-4 sm:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field icon={HiOutlineFolder}       label={t('cases.detail.fieldTitle', { entity: getEntity('case').singular })}>{caseData.title}</Field>
                <Field icon={HiOutlineUser}         label={t('cases.detail.fieldClient', { client: getEntity('client').singular })}>{caseData.client_name || 'N/A'}</Field>
                <Field icon={HiOutlineCalendarDays} label={t('cases.detail.fieldDate', { entity: getEntity('case').singular })}>{formatDate(caseData.date)}</Field>
                <Field icon={HiOutlineCalendarDays} label={t('cases.detail.fieldCreated')}>{formatDate(caseData.created_at)}</Field>
              </div>

              {caseData.description && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-ink-muted mb-1.5 inline-flex items-center gap-2">
                    <HiOutlineDocumentText className="w-4 h-4" />
                    {t('cases.detail.fieldDescription')}
                  </div>
                  <Card variant="bordered" padding="md">
                    <p className="text-sm text-ink-primary whitespace-pre-wrap">{caseData.description}</p>
                  </Card>
                </div>
              )}

              <Card padding="md">
                <Card.Header title={t('cases.detail.quickActions')} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <PermissionGuard resource="session" action="create">
                    <QuickAction
                      icon={HiOutlinePlus}
                      tone="brand"
                      title={t('cases.detail.addSession', { entity: getEntity('session').singular })}
                      description={t('cases.detail.addSessionDesc', { entity: getEntity('session').singular.toLowerCase() })}
                      onClick={() => navigate(`/sessions/create?case=${id}`)}
                    />
                  </PermissionGuard>
                  <PermissionGuard resource="document" action="create">
                    <BlockedAction action="upload_document">
                      <QuickAction
                        icon={HiOutlineDocumentText}
                        tone="info"
                        title={t('cases.detail.addDocument', { entity: getEntity('document').singular })}
                        description={t('cases.detail.addDocumentDesc', { entity: getEntity('case').singular.toLowerCase() })}
                        onClick={() => setShowUploadModal(true)}
                        fullWidth
                      />
                    </BlockedAction>
                  </PermissionGuard>
                  <PermissionGuard resource="case" action="update">
                    <QuickAction
                      icon={HiOutlinePencilSquare}
                      tone="neutral"
                      title={t('cases.detail.editCase', { entity: getEntity('case').singular })}
                      description={t('cases.detail.editCaseDesc', { entity: getEntity('case').singular.toLowerCase() })}
                      onClick={() => navigate(`/cases/${id}/edit`)}
                    />
                  </PermissionGuard>
                </div>
              </Card>

              <Card padding="md">
                <Card.Header title={t('cases.detail.statistics', { entity: getEntity('case').singular })} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Stat icon={HiOutlineClock}         label={t('cases.detail.totalSessions', { entity: getEntity('session').plural })} value={caseData?.sessions_count || 0} />
                  <Stat icon={HiOutlineDocumentText}  label={t('cases.detail.documentsLabel', { entity: getEntity('document').plural })} value={caseData?.documents_count || 0} />
                  <Stat icon={HiOutlineCalendarDays} label={t('cases.detail.createdLabel')}                                              value={formatDate(caseData.created_at)} />
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'sessions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-ink-primary">
                  {t('cases.detail.sessionsHeading', {
                    caseEntity: getEntity('case').singular,
                    sessionEntity: getEntity('session').plural,
                  })}
                </h3>
                <PermissionGuard resource="session" action="create">
                  <Button leadingIcon={HiOutlinePlus} onClick={() => navigate(`/sessions/create?case=${id}`)}>
                    {t('cases.detail.addSession', { entity: getEntity('session').singular })}
                  </Button>
                </PermissionGuard>
              </div>

              {sessionsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="border border-border rounded-lg p-4">
                      <div className="w-48 h-4 bg-muted rounded animate-pulse mb-2" />
                      <div className="w-32 h-3 bg-muted rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : sessions.length > 0 ? (
                <ul className="space-y-2">
                  {sessions.map((session) => (
                    <li
                      key={session.id}
                      className="border border-border rounded-lg p-4 hover:border-border-strong hover:bg-muted/30 transition-colors flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-ink-primary truncate">{session.title}</h4>
                        <p className="text-xs text-ink-muted">{formatDateTime(session.date)}</p>
                        {session.duration && (
                          <p className="text-xs text-ink-muted">{session.duration} {t('sessions.detail.minutes')}</p>
                        )}
                      </div>
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/sessions/${session.id}`)}>
                        {t('common.viewDetails')}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  icon={HiOutlineClock}
                  title={t('cases.detail.noSessionsYet', { entity: getEntity('session').plural.toLowerCase() })}
                  description={t('cases.detail.startTracking', {
                    entity: getEntity('session').plural.toLowerCase(),
                    caseEntity: getEntity('case').singular.toLowerCase(),
                  })}
                  action={
                    <PermissionGuard resource="session" action="create">
                      <Button leadingIcon={HiOutlinePlus} onClick={() => navigate(`/sessions/create?case=${id}`)}>
                        {t('cases.detail.addFirstSession', { entity: getEntity('session').singular.toLowerCase() })}
                      </Button>
                    </PermissionGuard>
                  }
                  compact
                />
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <DocumentTable
              context="case"
              contextId={parseInt(id)}
              contextName={caseData?.title}
              onDocumentUpdate={fetchCaseDetails}
              uploadModalProps={{
                clientId: caseData?.client_id,
                caseId: parseInt(id),
              }}
            />
          )}
        </div>
      </Card>

      <MessagePanel
        isOpen={showMessagePanel}
        onClose={() => setShowMessagePanel(false)}
        entityType="case"
        entityId={parseInt(id)}
        entityName={caseData?.title}
      />

      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          fetchCaseDetails()
          setShowUploadModal(false)
        }}
        preselectedClient={caseData?.client_id || caseData?.client?.id}
        preselectedCase={id}
      />
    </div>
  )
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-ink-muted mb-1.5 inline-flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <p className="text-sm text-ink-primary font-medium">{children}</p>
    </div>
  )
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-ink-muted flex-shrink-0" />}
        <span className="text-sm text-ink-secondary truncate">{label}</span>
      </div>
      <span className="text-sm font-semibold text-ink-primary text-end">{value}</span>
    </div>
  )
}

const QUICK_TONE = {
  brand:   'bg-brand-soft text-brand',
  info:    'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  neutral: 'bg-muted text-ink-secondary',
}

function QuickAction({ icon: Icon, tone = 'brand', title, description, onClick, fullWidth = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-start p-3 rounded-xl border border-border bg-surface hover:border-border-strong hover:shadow-token-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 group ${fullWidth ? 'w-full' : ''}`}
    >
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 ${QUICK_TONE[tone] || QUICK_TONE.brand}`}>
          {Icon && <Icon className="w-4 h-4" />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-primary">{title}</p>
          <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{description}</p>
        </div>
      </div>
    </button>
  )
}
